/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/


import React from 'react';
import { motion } from 'framer-motion';
import { Course } from '../types';
import { ArrowUpRight } from 'lucide-react';

interface ArtistCardProps {
  artist: Course; // Keeping prop name 'artist' to minimize ripple changes in parent, but it's a Course
  onClick: () => void;
}

const ArtistCard: React.FC<ArtistCardProps> = ({ artist: course, onClick }) => {
  return (
    <motion.div
      className="group relative h-[350px] md:h-[450px] w-full overflow-hidden border-b md:border-r border-white/10 bg-[#1e293b] cursor-pointer"
      initial="rest"
      whileHover="hover"
      whileTap="hover"
      animate="rest"
      data-hover="true"
      onClick={onClick}
    >
      {/* Image Background with Zoom */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.img 
          src={course.image} 
          alt={course.name} 
          className="h-full w-full object-cover grayscale opacity-60 will-change-transform"
          variants={{
            rest: { scale: 1, filter: 'grayscale(100%)' },
            hover: { scale: 1.05, filter: 'grayscale(0%)' }
          }}
          transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] to-transparent" />
      </div>

      {/* Overlay Info */}
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between pointer-events-none">
        <div className="flex justify-between items-start">
           <span className="text-xs font-mono font-bold border border-[#f97316] text-[#f97316] bg-black/50 px-2 py-1 backdrop-blur-md">
             {course.duration}
           </span>
           <motion.div
             variants={{
               rest: { opacity: 0, x: 20, y: -20 },
               hover: { opacity: 1, x: 0, y: 0 }
             }}
             className="bg-[#f97316] text-black rounded-none p-2 will-change-transform"
           >
             <ArrowUpRight className="w-6 h-6" />
           </motion.div>
        </div>

        <div>
          <div className="overflow-hidden">
            <motion.h3 
              className="font-heading text-2xl md:text-3xl font-bold uppercase text-white leading-tight will-change-transform"
              variants={{
                rest: { y: 0 },
                hover: { y: -5 }
              }}
              transition={{ duration: 0.4 }}
            >
              {course.name}
            </motion.h3>
          </div>
          <motion.p 
            className="text-xs font-bold uppercase tracking-widest text-gray-400 mt-2 will-change-transform"
            variants={{
              rest: { opacity: 0, y: 10 },
              hover: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {course.category}
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
};

export default ArtistCard;